import { useEffect, useRef, useState } from "react";
import { MapPin, Plane } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Glass-style address input with debounced autocomplete dropdown.
 */
export default function AddressInput({
  label,
  value,
  onChange,
  placeholder = "Pickup address…",
  testid,
  icon = "pin",
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const fetchSuggestions = (q) => {
    if (!q || q.length < 2) {
      setSuggestions([]);
      return;
    }
    api
      .get("/geo/autocomplete", { params: { q } })
      .then(({ data }) => {
        setSuggestions(data.results || []);
        setOpen(true);
        setActiveIdx(-1);
      })
      .catch(() => {
        setSuggestions([]);
      });
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  };

  const pick = (s) => {
    onChange(s.label);
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || !suggestions.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIdx >= 0) {
      e.preventDefault();
      pick(suggestions[activeIdx]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const Icon = icon === "plane" ? Plane : MapPin;

  return (
    <div className="relative" ref={containerRef}>
      <label className="block text-[11px] uppercase tracking-[0.25em] text-[#8E9AA6] mb-3">
        {label}
      </label>
      <div className="flex items-center gap-3 border-b border-white/20 focus-within:border-white transition-colors">
        <Icon size={16} strokeWidth={1.25} className="text-[#8E9AA6] shrink-0" />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onFocus={() => value && value.length >= 2 && fetchSuggestions(value)}
          onKeyDown={onKey}
          data-testid={testid}
          placeholder={placeholder}
          autoComplete="off"
          className="w-full bg-transparent px-0 py-3 text-white placeholder-[#8E9AA6] focus:outline-none font-sans text-base"
        />
      </div>

      {open && suggestions.length > 0 && (
        <ul
          data-testid={`${testid}-suggestions`}
          className="absolute z-30 left-0 right-0 mt-2 max-h-72 overflow-auto backdrop-blur-2xl bg-[#0B0B0C]/95 border border-white/15 shadow-2xl"
        >
          {suggestions.map((s, i) => (
            <li
              key={s.label}
              onMouseDown={(e) => {
                e.preventDefault();
                pick(s);
              }}
              onMouseEnter={() => setActiveIdx(i)}
              data-testid={`${testid}-option-${i}`}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer text-sm transition-colors ${
                activeIdx === i ? "bg-white/10 text-white" : "text-[#E6E6E6] hover:bg-white/5"
              }`}
            >
              {s.is_airport ? (
                <Plane size={14} strokeWidth={1.25} className="text-[#8E9AA6]" />
              ) : (
                <MapPin size={14} strokeWidth={1.25} className="text-[#8E9AA6]" />
              )}
              <span>{s.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
